// src/components/BrokerProjectsPage.jsx
import { formatDisplayName } from "../utils/displayName";
import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, Loader2, MoreHorizontal, Search, X } from "lucide-react";
import { List, LayoutGrid, Filter, Pencil, Trash2 } from "lucide-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import BrokerDashboardLayout from "../components/BrokerDashboardLayout"; //  layout with sidebar
import { formatExpectedTiming } from "../utils/timeFormat";
import { isTimeoutError, withTimeout } from "../utils/asyncTimeout";
import { PROJECT_STATUSES, STATUS_TONE, getStatusSortIndex, normalizeProjectStatus } from "../utils/projectStatus";

const PROJECTS_TIMEOUT_MS = 25000;

/** Frosted panel utility (same as dashboard) */
const PANEL =
  "rounded-2xl border border-white/15 bg-white/70 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.08)]";

/** Sorting orders (match dashboard) */
const STATUS_ORDER   = PROJECT_STATUSES;
const PRIORITY_ORDER = ["Urgent", "Medium", "Low"];

const normStatus = (s) => normalizeProjectStatus(s);
const DATE_FILTERS = [
  { value: "all", label: "All dates" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "year", label: "This year" },
];
const getParamValue = (params, key, fallback, allowedValues) => {
  const value = params.get(key);
  return allowedValues.includes(value) ? value : fallback;
};
const getPositiveNumberParam = (params, key, fallback, allowedValues = null) => {
  const value = Number(params.get(key));
  if (!Number.isFinite(value) || value < 1) return fallback;
  return allowedValues && !allowedValues.includes(value) ? fallback : value;
};
const getActiveFilterSummary = (filters) => {
  const labels = [];
  if (filters.status !== "all") labels.push(`Status: ${filters.status}`);
  if (filters.priority !== "all") labels.push(`Priority: ${filters.priority}`);
  if (filters.dateRange !== "all") {
    labels.push(DATE_FILTERS.find((item) => item.value === filters.dateRange)?.label || filters.dateRange);
  }
  if (labels.length === 0) return "Filters";
  if (labels.length === 1) return labels[0];
  return `${labels[0]} +${labels.length - 1}`;
};
const getSortLabel = (sort) => {
  const labels = {
    modified: "Last modified",
    tail: "Tail number",
    status: "Status",
    priority: "Priority",
    requested: "Requested date",
  };
  return labels[sort.by] || "Last modified";
};

export default function BrokerProjectsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const initialFilters = {
    status: getParamValue(params, "status", "all", ["all", ...STATUS_ORDER]),
    priority: getParamValue(params, "priority", "all", ["all", ...PRIORITY_ORDER]),
    dateRange: getParamValue(params, "date", "all", DATE_FILTERS.map((item) => item.value)),
  };
  const hasActiveInitialFilters = Object.values(initialFilters).some((value) => value !== "all");
  
  const [viewMode, setViewMode] = useState(getParamValue(params, "view", "list", ["list", "card"]));
const [openMenuId, setOpenMenuId] = useState(null);
const [showFilters, setShowFilters] = useState(params.get("filters") === "1" || hasActiveInitialFilters);
const [toast, setToast] = useState(null);
const toastTimerRef = useRef(null);
const [brokerAccountId, setBrokerAccountId] = useState("");

  // ---------- table state ----------
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorText, setErrorText] = useState("");

  // sorting
  const [sort, setSort] = useState({
    by: getParamValue(params, "sort", "modified", ["modified", "tail", "status", "priority", "requested"]),
    dir: getParamValue(params, "dir", "desc", ["asc", "desc"]),
  });
  const toggleSort = (by) =>
    setSort((p) => (p.by === by ? { by, dir: p.dir === "asc" ? "desc" : "asc" } : { by, dir: "asc" }));
  const [filters, setFilters] = useState(initialFilters);

  // pagination
  const [page, setPage] = useState(getPositiveNumberParam(params, "page", 1));
  const [pageSize, setPageSize] = useState(getPositiveNumberParam(params, "pageSize", 10, [10, 20, 50]));
  const [total, setTotal] = useState(0);

  // search + autocomplete
  const [q, setQ] = useState(params.get("q") || params.get("tail") || "");
  const [openAC, setOpenAC] = useState(false);
  const [acLoading, setAcLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const boxRef = useRef(null);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const { data: auth } = await withTimeout(
          supabase.auth.getUser(),
          PROJECTS_TIMEOUT_MS,
          "Projects session check timed out"
        );
        const userId = auth?.user?.id;
        if (!userId) {
          if (alive) setBrokerAccountId("");
          return;
        }

        const { data, error } = await withTimeout(
          supabase
            .from("broker_account_members")
            .select("broker_account_id, role")
            .eq("user_id", userId)
            .order("role", { ascending: false })
            .limit(1)
            .maybeSingle(),
          PROJECTS_TIMEOUT_MS,
          "Broker account lookup timed out"
        );

        if (!alive) return;

        if (error) {
          setErrorText(error.message || "Could not load broker account access.");
          setBrokerAccountId("");
          return;
        }

        setBrokerAccountId(data?.broker_account_id || "");
      } catch (error) {
        if (!alive) return;
        setErrorText(
          isTimeoutError(error)
            ? "Projects took too long to load. Refresh the page or sign out and back in."
            : "Could not load broker account access."
        );
        setBrokerAccountId("");
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const next = new URLSearchParams(location.search);
    q ? next.set("q", q) : next.delete("q");
    next.delete("tail");
    viewMode !== "list" ? next.set("view", viewMode) : next.delete("view");
    showFilters ? next.set("filters", "1") : next.delete("filters");
    filters.status !== "all" ? next.set("status", filters.status) : next.delete("status");
    filters.priority !== "all" ? next.set("priority", filters.priority) : next.delete("priority");
    filters.dateRange !== "all" ? next.set("date", filters.dateRange) : next.delete("date");
    sort.by !== "modified" ? next.set("sort", sort.by) : next.delete("sort");
    sort.dir !== "desc" ? next.set("dir", sort.dir) : next.delete("dir");
    page > 1 ? next.set("page", String(page)) : next.delete("page");
    pageSize !== 10 ? next.set("pageSize", String(pageSize)) : next.delete("pageSize");

    if (next.toString() !== params.toString()) {
      setParams(next, { replace: true });
    }
  }, [filters, location.search, page, pageSize, params, q, setParams, showFilters, sort, viewMode]);

  // close AC on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpenAC(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  // ---------- fetch suggestions ----------
  const debouncedQ = useMemo(() => q.trim(), [q]);
  useEffect(() => {
    let alive = true;
    (async () => {
      const term = debouncedQ;
      if (!term || !brokerAccountId) { setSuggestions([]); return; }
      setAcLoading(true);
      try {
        const { data, error } = await withTimeout(
          supabase
            .from("project_status")
            .select("tail_number,broker_intake!inner(broker_account_id)")
            .eq("broker_intake.broker_account_id", brokerAccountId)
            .ilike("tail_number", `%${term}%`)
            .not("tail_number", "is", null)
            .limit(8),
          PROJECTS_TIMEOUT_MS,
          "Project search timed out"
        );
        if (!alive) return;
        if (error) { setSuggestions([]); return; }
        const uniq = [...new Map((data || []).map(d => [d.tail_number, d])).keys()];
        setSuggestions(uniq);
        setOpenAC(true);
      } catch {
        if (alive) setSuggestions([]);
      } finally {
        if (alive) setAcLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [brokerAccountId, debouncedQ]);

useEffect(() => {
  const handleClickOutside = (e) => {
    if (!e.target.closest(".kebab-menu")) {
      setOpenMenuId(null);
    }
  };
  document.addEventListener("click", handleClickOutside);
  return () => document.removeEventListener("click", handleClickOutside);
}, []);


  // ---------- fetch table data ----------
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErrorText("");

      if (!brokerAccountId) {
        setRows([]);
        setTotal(0);
        setLoading(false);
        return;
      }

      const SELECT = "id,tail_number,current_status,priority,assigned_pilot,expected_datetime,timezone,updated_at,broker_intake!inner(id,created_at,services,broker_account_id)";

      let query = supabase.from("project_status").select(SELECT);
      query = query.eq("broker_intake.broker_account_id", brokerAccountId);
      if (debouncedQ) query = query.ilike("tail_number", `%${debouncedQ}%`);
      query = query.order("updated_at", { ascending: false }).limit(1000);

      try {
        const { data, error } = await withTimeout(
          query,
          PROJECTS_TIMEOUT_MS,
          "Projects lookup timed out"
        );
        if (!alive) return;

        if (error) {
          setErrorText(error.message || "Failed to load.");
          setRows([]); setTotal(0); setLoading(false);
          return;
        }

        let filtered = data || [];

      if (filters.status !== "all") {
        filtered = filtered.filter((row) => normStatus(row.current_status) === filters.status);
      }

      if (filters.priority !== "all") {
        filtered = filtered.filter((row) => row.priority === filters.priority);
      }

      if (filters.dateRange !== "all") {
        const now = new Date();
        let start = null;
        if (filters.dateRange === "30d") {
          start = new Date();
          start.setDate(now.getDate() - 30);
        } else if (filters.dateRange === "90d") {
          start = new Date();
          start.setDate(now.getDate() - 90);
        } else if (filters.dateRange === "year") {
          start = new Date(now.getFullYear(), 0, 1);
        }
        if (start) {
          filtered = filtered.filter((row) => {
            const createdAt = row?.broker_intake?.created_at;
            return createdAt && new Date(createdAt) >= start;
          });
        }
      }

      const factor = sort.dir === "asc" ? 1 : -1;
      const sorted = [...filtered].sort((a, b) => {
        if (sort.by === "status") {
          return (getStatusSortIndex(a.current_status) - getStatusSortIndex(b.current_status)) * factor;
        }
        if (sort.by === "priority") {
          const A = PRIORITY_ORDER.indexOf(a.priority || "");
          const B = PRIORITY_ORDER.indexOf(b.priority || "");
          return (A - B) * factor;
        }
        if (sort.by === "requested") {
          const A = new Date(a?.broker_intake?.created_at || 0).getTime();
          const B = new Date(b?.broker_intake?.created_at || 0).getTime();
          return (A - B) * factor;
        }
        if (sort.by === "modified") {
          const A = new Date(a.updated_at || a?.broker_intake?.created_at || 0).getTime();
          const B = new Date(b.updated_at || b?.broker_intake?.created_at || 0).getTime();
          return (A - B) * factor;
        }
        return String(a.tail_number || "").localeCompare(String(b.tail_number || "")) * factor;
      });

        setTotal(sorted.length);
        const safePage = Math.max(1, Math.min(page, Math.max(1, Math.ceil(sorted.length / pageSize))));
        const from = (safePage - 1) * pageSize;
        const to = from + pageSize;
        setRows(sorted.slice(from, to));
        if (safePage !== page) setPage(safePage);
        setLoading(false);
      } catch (error) {
        if (!alive) return;
        setRows([]);
        setTotal(0);
        setErrorText(
          isTimeoutError(error)
            ? "Projects took too long to load. Refresh the page or sign out and back in."
            : "Failed to load projects."
        );
        setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [brokerAccountId, page, pageSize, debouncedQ, sort, filters]);

  const setFilterValue = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ status: "all", priority: "all", dateRange: "all" });
    setPage(1);
  };

  const showToast = (title, message) => {
    setToast({ title, message });
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(null), 5000);
  };

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (location.state?.message) {
      showToast(location.state.title || "Deleted", location.state.message);
      navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: {} });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  const openProject = (project) => {
    navigate(`/projects/${encodeURIComponent(project.tail_number || "")}`, {
      state: { from: `${location.pathname}${location.search}` },
    });
  };

  const deleteProject = async (project) => {
    const confirmed = window.confirm(`Delete project request for ${project.tail_number}? This cannot be undone.`);
    if (!confirmed) return;

    setOpenMenuId(null);
    const { error } = await withTimeout(
      supabase
        .from("broker_intake")
        .delete()
        .eq("id", project?.broker_intake?.id),
      PROJECTS_TIMEOUT_MS,
      "Project delete timed out"
    );

    if (error) {
      setErrorText(error.message || "Could not delete project.");
      return;
    }

    showToast("Deleted", `${project.tail_number} was deleted.`);
    setPage(1);
    setFilters((current) => ({ ...current }));
  };

  // ---------- small UI helpers ----------
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const canPrev = page > 1;
  const canNext = page < totalPages;
  const filterButtonLabel = getActiveFilterSummary(filters);
  const sortLabel = getSortLabel(sort);

  const PRIORITY_BG = {
    Urgent: "rgba(211, 34, 43, 0.80)",
    Medium: "rgba(63, 63, 63, 0.80)",
    Low: "rgba(131, 131, 132, 0.80)",
  };

  const StatusPill = ({ status }) => {
    const s = normStatus(status);
    const tone = STATUS_TONE[s] || "rgba(0,0,0,0.25)";
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] sm:text-xs font-medium"
            style={{ color: tone, background: "rgba(0,0,0,0.035)", boxShadow: `inset 0 0 0 1px ${tone}22` }}>
        <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: tone }} />
        {s || "—"}
      </span>
    );
  };

  const PriorityPill = ({ priority }) => {
    const bg = PRIORITY_BG[priority] || "rgba(0,0,0,0.06)";
    return (
      <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] sm:text-xs font-semibold"
            style={{ background: bg, color: "#FFFFFF" }}>
        {priority || "—"}
      </span>
    );
  };

  return (
    <BrokerDashboardLayout>
  <div className="space-y-6 text-[#1A1A1A]">
        {toast && (
          <Toast title={toast.title} message={toast.message} onClose={() => setToast(null)} />
        )}

        {/* Title + Search */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight">Projects</h1>
            <p className="text-xs text-black/60 mt-1">All projects • {sortLabel} • {total.toLocaleString()} total</p>
          </div>

          {/* Toggle + Search + Filters */}
<div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">

  {/* Toggle */}
  <div className="flex border border-[#D9D9D9] rounded-md overflow-hidden text-sm font-medium h-10">

    <button
      onClick={() => setViewMode("list")}
      className={`flex items-center gap-1 px-4 h-full transition ${
        viewMode === "list"
          ? "bg-strobe-red text-white"
          : "bg-white text-black hover:bg-strobe-red/10"
      }`}
    >
      <List className="w-4 h-4" /> List
    </button>
    <button
      onClick={() => setViewMode("card")}
      className={`flex items-center gap-1 px-4 py-1 transition ${
        viewMode === "card"
          ? "bg-strobe-red text-white"
          : "bg-white text-black hover:bg-strobe-red/10"
      }`}
    >
      <LayoutGrid className="w-4 h-4" /> Cards
    </button>
  </div>

  {/* Filters */}
  <button
    type="button"
    onClick={() => setShowFilters((current) => !current)}
    className={`border border-[#D9D9D9] rounded-md text-sm font-medium text-black hover:bg-strobe-red/10 h-10 px-4 inline-flex items-center ${showFilters ? "bg-white" : ""}`}
  >
  <Filter className="w-4 h-4 mr-1.5" /> {filterButtonLabel}
</button>


  {/* Search */}
  <div ref={boxRef} className="relative w-full sm:w-64">
    <div className={`${PANEL} w-full px-3 py-2 flex items-center gap-2`}>
      <Search className="w-4 h-4 shrink-0 text-black/35" strokeWidth={2} />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => q && setOpenAC(true)}
        placeholder="Search tail #"
        className="w-full bg-transparent outline-none text-sm placeholder-black/50"
        aria-label="Search tail number"
      />
      {acLoading ? (
        <Loader2 className="w-4 h-4 animate-spin opacity-70" />
      ) : q ? (
        <button onClick={() => { setQ(""); setOpenAC(false); }} aria-label="Clear" className="opacity-70 hover:opacity-100">
          <X className="w-4 h-4" />
        </button>
      ) : null}
    </div>

    {openAC && suggestions.length > 0 && (
      <div className="absolute z-20 mt-1 w-full rounded-xl border border-white/15 bg-white/80 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.08)] overflow-hidden">
        {suggestions.map((t) => (
          <button
            key={t}
            onClick={() => { setQ(t); setOpenAC(false); setPage(1); }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-black/[0.04]"
          >
            {t}
          </button>
        ))}
      </div>
    )}
    {openAC && !acLoading && suggestions.length === 0 && q && (
      <div className="absolute z-20 mt-1 w-full rounded-xl border border-white/15 bg-white/80 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.08)] px-3 py-2 text-sm text-black/60">
        No matching tail numbers
      </div>
    )}
  </div>
</div>
</div>

{showFilters && (
  <div className={`${PANEL} p-4`}>
    <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-black/45">Status</label>
        <select
          value={filters.status}
          onChange={(e) => setFilterValue("status", e.target.value)}
          className="w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm"
        >
          <option value="all">All statuses</option>
          {STATUS_ORDER.map((status) => <option key={status} value={status}>{status}</option>)}
        </select>
      </div>
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-black/45">Priority</label>
        <select
          value={filters.priority}
          onChange={(e) => setFilterValue("priority", e.target.value)}
          className="w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm"
        >
          <option value="all">All priorities</option>
          {PRIORITY_ORDER.map((priority) => <option key={priority} value={priority}>{priority}</option>)}
        </select>
      </div>
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-black/45">Requested Date</label>
        <select
          value={filters.dateRange}
          onChange={(e) => setFilterValue("dateRange", e.target.value)}
          className="w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm"
        >
          {DATE_FILTERS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
        </select>
      </div>
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-black/45">Sort</label>
        <div className="grid grid-cols-[1fr_auto] gap-2">
          <select
            value={sort.by}
            onChange={(e) => { setSort({ by: e.target.value, dir: e.target.value === "modified" ? "desc" : "asc" }); setPage(1); }}
            className="w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm"
          >
            <option value="modified">Last Modified</option>
            <option value="tail">Tail Number</option>
            <option value="status">Status</option>
            <option value="priority">Priority</option>
            <option value="requested">Requested Date</option>
          </select>
          <button
            type="button"
            onClick={() => setSort((current) => ({ ...current, dir: current.dir === "asc" ? "desc" : "asc" }))}
            className="rounded-md border border-black/10 bg-white px-3 py-2 text-sm font-semibold hover:bg-black/[0.03]"
          >
            {sort.dir === "asc" ? "Asc" : "Desc"}
          </button>
        </div>
      </div>
    </div>
    <div className="mt-3 flex justify-end">
      <button
        type="button"
        onClick={clearFilters}
        className="text-sm font-medium text-black/55 hover:text-black"
      >
        Clear filters
      </button>
    </div>
  </div>
)}

        {/* Table or Card View */}
{viewMode === "list" ? (
  <div className={`${PANEL} overflow-hidden`}>
    <div className="overflow-auto">
      <table className="w-full text-left">
        
        <tbody>
          {loading && (
            <tr><td colSpan={5} className="py-8 text-center text-black/60">Loading…</td></tr>
          )}
          {!loading && errorText && (
            <tr><td colSpan={5} className="py-8 text-center text-rose-600">{errorText}</td></tr>
          )}
          {!loading && !errorText && rows.length === 0 && (
            <tr><td colSpan={5} className="py-8 text-center text-black/50">No projects found.</td></tr>
          )}
          {!loading && !errorText && rows.map((p) => (
            <tr
  key={p.id}
  onClick={() => {
    openProject(p);
  }}
  className="border-b border-black/10 hover:bg-black/[0.02] transition cursor-pointer"
>

              <td colSpan={5} className="px-6 py-5">
  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

    {/* LEFT */}
    <div className="space-y-2">
      <div className="text-sm font-semibold">
        Tail Number: <span className="font-bold">{p.tail_number || "—"}</span>
      </div>

      <div className="flex items-center gap-2 text-xs">
        <span className="text-black/50">Status:</span>
        <StatusPill status={p.current_status} />
      </div>

      <div className="text-xs text-black/60">
        Expected By:{" "}
        {formatExpectedTiming(p.expected_datetime, p.timezone)}
      </div>
    </div>

    {/* MIDDLE */}
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs">
        <span className="text-black/50">Priority:</span>
        <PriorityPill priority={p.priority} />
      </div>

      <div className="text-xs text-black/60 leading-relaxed">
        <span className="block text-black/50 mb-1">Services:</span>
        {p?.broker_intake?.services || "—"}
      </div>
    </div>

    {/* RIGHT */}
    <div className="space-y-2 text-xs relative">
  <div>
    <span className="text-black/50">Assigned to:</span>{" "}
    <span className="font-medium">{formatDisplayName(p.assigned_pilot) || "—"}</span>
  </div>

  <div>
    <span className="text-black/50">Requested:</span>{" "}
    {p?.broker_intake?.created_at
      ? new Date(p.broker_intake.created_at).toLocaleDateString()
      : "—"}
  </div>

  {/* Kebab icon */}
 {(() => {
  const isEditable =
    !p.expected_datetime ||
    new Date(p.expected_datetime).getTime() - Date.now() > 86400000;

  return (
    <div className="absolute -top-4 right-0 kebab-menu">
      <button
  onClick={(e) => {
    e.stopPropagation(); // 🔥 prevents row navigation
    setOpenMenuId(openMenuId === p.id ? null : p.id);
  }}
  className="inline-flex h-9 w-9 items-center justify-center rounded-md text-black hover:bg-black/[0.04]"
  aria-label={`Actions for ${p.tail_number}`}
>
  <MoreHorizontal className="h-5 w-5" />
</button>


      {openMenuId === p.id && (
        <div className="absolute right-0 mt-1 w-36 bg-white border border-black/10 rounded-md shadow-md z-10">
          <button
            onClick={(e) => {
  e.stopPropagation();
  setOpenMenuId(null);
  openProject(p);
}}
            className="inline-flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-black/5"
          >
            <Pencil className="h-4 w-4" />
            Edit
          </button>
          <button
            onClick={(e) => {
  e.stopPropagation();
  if (isEditable) deleteProject(p);
}}
            disabled={!isEditable}
            className="inline-flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>
      )}
    </div>
  );
})()}

</div>
  </div>
</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
) : (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
    {loading && (
      <div className="col-span-full text-center text-black/60 py-8">Loading…</div>
    )}
    {!loading && errorText && (
      <div className="col-span-full text-center text-rose-600 py-8">{errorText}</div>
    )}
    {!loading && !errorText && rows.length === 0 && (
      <div className="col-span-full text-center text-black/50 py-8">No projects found.</div>
    )}
    {!loading && !errorText && rows.map((p) => (
      <button
        key={p.id}
        onClick={() => openProject(p)}
        className="text-left rounded-xl border border-black/10 bg-white px-6 py-4 shadow hover:bg-black/[0.02] transition duration-200"
      >
        <div className="text-sm font-bold mb-1">{p.tail_number || "—"}</div>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <PriorityPill priority={p.priority} />
          <StatusPill status={p.current_status} />
        </div>
        <div className="text-xs text-black/70 mb-1">
          <strong>Expected By:</strong>{" "}
          {formatExpectedTiming(p.expected_datetime, p.timezone)}
        </div>

        <div className="text-xs text-black/70 mb-1">
          <strong>Services:</strong>{" "}
          {p?.broker_intake?.services || "—"}
        </div>

        <div className="mt-10 text-xs text-black/70 mb-1">
          <strong>Assigned to:</strong>{" "}
          {formatDisplayName(p.assigned_pilot) || "—"}
        </div>

        <div className="text-xs text-black/70">
          <strong>Requested:</strong>{" "}
          {p?.broker_intake?.created_at ? new Date(p.broker_intake.created_at).toLocaleDateString() : "—"}
        </div>

      </button>
    ))}
  </div>
)}


          {/* Pagination */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 border-t border-black/10 bg-white/60">
            <div className="text-xs text-black/60">
              Page <span className="font-medium">{page}</span> of <span className="font-medium">{totalPages}</span> •{" "}
              <span className="font-medium">{total.toLocaleString()}</span> results
            </div>

            <div className="flex items-center gap-2">
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="text-xs rounded-md border border-black/10 bg-white px-2 py-1"
              >
                {[10, 20, 50].map(n => <option key={n} value={n}>{n} / page</option>)}
              </select>

              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={!canPrev}
                className={`text-xs px-3 py-1 rounded-md border ${canPrev ? "border-black/20 hover:bg-black/[0.04]" : "border-black/10 opacity-40 cursor-not-allowed"}`}
              >
                Prev
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={!canNext}
                className={`text-xs px-3 py-1 rounded-md border ${canNext ? "border-black/20 hover:bg-black/[0.04]" : "border-black/10 opacity-40 cursor-not-allowed"}`}
              >
                Next
              </button>
            </div>
          </div>
        </div>
        
    </BrokerDashboardLayout>
  );
}

function Toast({ title, message, onClose }) {
  return (
    <div className="fixed right-4 top-4 z-50 w-[min(92vw,360px)] rounded-xl border border-black/10 bg-white p-4 shadow-2xl">
      <button
        type="button"
        onClick={onClose}
        className="absolute right-2 top-2 rounded p-1 text-black/50 hover:bg-black/5 hover:text-black"
        aria-label="Dismiss message"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex gap-3 pr-5">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#EB040D]" />
        <div>
          <p className="text-sm font-semibold text-[#1A1A1A]">{title}</p>
          <p className="mt-1 text-sm text-black/65">{message}</p>
        </div>
      </div>
    </div>
  );
}
